import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Avatar,
  Text,
  Box,
  Center,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { Heart } from 'lucide-react';
import type { MinimalUser } from '@/types/social';

interface LikesModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: MinimalUser[];
}

export function LikesModal({ isOpen, onClose, users }: LikesModalProps) {
  const navigate = useNavigate();

  const handleUserClick = (userId: number) => {
    navigate({ to: '/profile/$userId', params: { userId } });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md' isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4} minWidth={{ md: "30%" }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="pink.100" borderRadius="var(--r-chip)" p={2}>
              <Heart size={20} color="var(--chakra-colors-pink-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Liked by
              </Text>
              <Text fontSize="xs" color="var(--card-text-2)" fontWeight="normal">
                {users.length} {users.length === 1 ? 'person' : 'people'}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {users.length === 0 ? (
            <Center py={8}>
              <Text color="var(--card-text-2)">No likes yet</Text>
            </Center>
          ) : (
            <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto" pr={2}>
              {users.map((user) => (
                <Box
                  key={user.id}
                  p={3}
                  borderRadius="var(--r-chip)"
                  bg="var(--card-surface-2)"
                  _hover={{ bg: 'gray.100', cursor: 'pointer' }}
                  onClick={() => handleUserClick(user.id)}
                >
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={user.name}
                      src={user.photoUrl || undefined}
                    />
                    <Text fontWeight="medium">{user.name}</Text>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
